import React                from 'react';

import * as AgencyType      from 'shared/types/Agency';
import * as ManifestType    from 'shared/types/Manifest';
import * as VehicleType     from 'shared/types/Vehicle';
import * as OrderType       from 'shared/types/Order';
import * as UserType        from 'shared/types/User';
import dayjs                from 'shared/utils/day-timezone';

import tectransit           from 'utils/TecTransit';

import { DroppableManifestCell } from './DraggableDroppableWrapper';

export interface VehicleManifestsProps {
    vehicleManifests    : ManifestType.VehicleManifest.Hydrated<OrderType.Order<string>>[];
    vehiclesById        : Record<number,VehicleType.Vehicle>;
    driversById?        : Record<number,UserType.User>;
    onDropOnVehicle?    : ( vehicle_id:number, draggable:Record<string,any>, schedulable:OrderType.Order<string> ) => Record<string,any>;
    // Required formatters
    getStops            : ( ndx:number ) => React.ReactNode;
    getDistance         : ( ndx:number ) => React.ReactNode;
    getStatus           : ( ndx:number ) => React.ReactNode;
    getSteps            : ( ndx:number ) => React.ReactNode;
    // Optional formatters
    getDurations?       : ( ndx:number ) => React.ReactNode;
    getFleetNumber?     : ( ndx:number ) => React.ReactNode;
    getDepot?           : ( ndx:number ) => React.ReactNode;
    getRequestedAt?     : ( ndx:number ) => React.ReactNode;    // Likely to be removed per issue #445
}
export const VehicleManifests : React.FC<VehicleManifestsProps> = props => {
    const scheduleByVehicleId   = AgencyType.rehashSchedule(
        tectransit.agency.schedule_by_driver_id,
        (driverId,vehicleId) => vehicleId,
        (driverId,vehicleId) => driverId
    );  
    const getFleetNumber = props.getFleetNumber || ((ndx:number) => {
        const vm = props.vehicleManifests[ndx];
        return <strong>{vm.vehicle_id}</strong>
    });
    const getDepot = props.getDepot || ((ndx:number) => {
        const vm = props.vehicleManifests[ndx];
        return <strong>{props.vehiclesById[vm.vehicle_id]?.depot_name||'??'}</strong>
    });
    const getStops = props.getStops || ((ndx:number) => {
        return <strong>n/a</strong>;
    });
    const getDistance = props.getDistance || ((ndx:number) => {
        const vm = props.vehicleManifests[ndx];
        return <strong>{vm.distance}</strong>;
    });
    const getDurations = props.getDurations || ((ndx:number) => {
        const vm                        = props.vehicleManifests[ndx];
        const driverScheduleByWeekday   = scheduleByVehicleId[vm.vehicle_id]||{};
        const info = vm.steps.reduce((acc,step) => {
            // Accummulating by the step name
            acc.total += step.duration||0;
            acc.by_name[step.name]      = (acc.by_name[step.name]||0)   + (step.duration||0);
            // Accumulating by the driver name is a whole lot more difficult
            const stepStartMoment   = step.seconds    ? dayjs(step.seconds*1000).tz(tectransit.agency.time_zone) : undefined;
            if( !stepStartMoment )
                return acc;
            const stepRanges        = driverScheduleByWeekday[stepStartMoment.format('dddd').toLowerCase()];
            if( !stepRanges )
                return acc;
            const stepEndMoment     = step.seconds    ? dayjs((step.seconds+(step.duration??0))*1000).tz(tectransit.agency.time_zone) : stepStartMoment;
            const stepStartMinutes  = stepStartMoment.diff(stepStartMoment.startOf('day'),'minutes');
            const stepEndMinutes    = stepEndMoment.diff(stepStartMoment.startOf('day'),'minutes');
            acc.by_driverid = stepRanges.reduce( (byDriverId,sr) => {
                const overlapStartMin   = Math.max(sr[0],stepStartMinutes);
                const overlapEndMin     = Math.min(sr[1],stepEndMinutes);
                if( overlapStartMin>=overlapEndMin )
                    return byDriverId; // the step and the driver schedule range do not overlap
                const driverId          = sr[2]??-1;
                byDriverId[driverId]    = (byDriverId[driverId]||0)+ ((overlapEndMin-overlapStartMin)*60);
                return byDriverId;
            },acc.by_driverid);
            return acc;
        },{
            total       : 0,
            by_name     : {} as Record<string,number>,
            by_driverid : {} as Record<number,number>
        });
        const formatSeconds = (secs:number) => {
            if( secs===24*3600 )
                return `24h`;
            return dayjs.duration(secs,'seconds').format('HH[h]mm[m]');
        }
        const by_name_title = Object.entries(info.by_name)
            .map(([key,secs])=>`${key} : ${formatSeconds(secs)}`)
            .join('\n');
        const by_driverid_title = props.driversById ? Object.entries(info.by_driverid)
            .map(([key,secs])=>`${props.driversById![Number(key)]?.name||'??'} : ${formatSeconds(secs)}`)
            .join('\n') : '';

        const title = by_driverid_title ? `By step name:\n${by_name_title}\n\nBy driver name:\n${by_driverid_title}` : `By step name:\n${by_name_title}` ;
        return <span title={title}>{formatSeconds(info.total)}</span>;
    });
    const getStatus = props.getStatus || ((ndx:number) => {
        return <strong>n/a</strong>;
    });
    const getSteps = props.getSteps || ((ndx:number) => {
        const vm = props.vehicleManifests[ndx];
        return <strong>{vm.steps.length}</strong>;
    });
    const onDropOnVehicle = (vehicle_id:number,draggable:Record<string,any>,schedulable:OrderType.Order<string>) => {
        return props.onDropOnVehicle ? props.onDropOnVehicle(vehicle_id,draggable,schedulable) : {};
    }
    return (
        <table style={{/*'border':'1px solid black',*/'width':'100%'}}>
            {(props.vehicleManifests.length>0) ? (
                <tbody style={{verticalAlign:"top",textAlign:'left'}}>
                    <tr>
                        <td>Fleet# </td>
                        {props.vehicleManifests.map((vm, ndx) => (
                            <DroppableManifestCell key={vm._id} droppableArgs={{ id: `manifest-${vm._id}`, data:(draggable:Record<string,any>,schedulable:OrderType.Order<string>) => {
                                return onDropOnVehicle(vm.vehicle_id,draggable,schedulable);
                            } }}>
                                {getFleetNumber(ndx)}
                            </DroppableManifestCell>
                        ))}
                    </tr>
                    <tr>
                        <td>Depot</td>
                        {props.vehicleManifests.map((vm, ndx) => (
                            <DroppableManifestCell key={vm._id} droppableArgs={{ id: `manifest-${vm._id}`, data: (draggable:Record<string,any>,schedulable:OrderType.Order<string>) => {
                                return onDropOnVehicle(vm.vehicle_id,draggable,schedulable);
                            } }}>
                                {getDepot(ndx)}
                            </DroppableManifestCell>
                        ))}
                    </tr>
                    <tr>
                        <td>Stops</td>
                        {props.vehicleManifests.map((vm, ndx) => (
                            <DroppableManifestCell key={vm._id} droppableArgs={{ id: `manifest-${vm._id}`, data: (draggable:Record<string,any>,schedulable:OrderType.Order<string>) => {
                                return onDropOnVehicle(vm.vehicle_id,draggable,schedulable);
                            } }}>
                                {getStops(ndx)}
                            </DroppableManifestCell>
                        ))}
                    </tr>
                    <tr>
                        <td>Distance</td>
                        {props.vehicleManifests.map((vm, ndx) => (
                            <DroppableManifestCell key={vm._id} droppableArgs={{ id: `manifest-${vm._id}`, data: (draggable:Record<string,any>,schedulable:OrderType.Order<string>) => {
                                return onDropOnVehicle(vm.vehicle_id,draggable,schedulable);
                            } }}>
                                {getDistance(ndx)}
                            </DroppableManifestCell>
                        ))}
                    </tr>
                    <tr>
                        <td>Durations</td>
                        {props.vehicleManifests.map((vm, ndx) => (
                            <DroppableManifestCell key={vm._id} droppableArgs={{ id: `manifest-${vm._id}`, data: (draggable:Record<string,any>,schedulable:OrderType.Order<string>) => {
                                return onDropOnVehicle(vm.vehicle_id,draggable,schedulable);
                            } }}>
                                {getDurations(ndx)}
                            </DroppableManifestCell>
                        ))}
                    </tr>
                    <tr>
                        <td>Status</td>
                        {props.vehicleManifests.map((vm, ndx) => (
                            <DroppableManifestCell key={vm._id} droppableArgs={{ id: `manifest-${vm._id}`, data: (draggable:Record<string,any>,schedulable:OrderType.Order<string>) => {
                                return onDropOnVehicle(vm.vehicle_id,draggable,schedulable);
                            } }}>
                                {getStatus(ndx)}
                            </DroppableManifestCell>
                        ))}
                    </tr>
                    <tr style={{ verticalAlign: "top" }}>
                        <td>Steps</td>
                        {props.vehicleManifests.map((vm, ndx) => (
                            <DroppableManifestCell key={vm._id} droppableArgs={{ id: `manifest-${vm._id}`, data: (draggable:Record<string,any>,schedulable:OrderType.Order<string>) => {
                                return onDropOnVehicle(vm.vehicle_id,draggable,schedulable);
                            } }}>
                                {getSteps(ndx)}
                            </DroppableManifestCell>
                        ))}
                    </tr>
                    <tr>
                        <th align="center" colSpan={props.vehicleManifests.length + 1}>
                            {props.vehicleManifests.length} Manifests
                        </th>
                    </tr>
                </tbody>
            ) : (
                <tbody style={{verticalAlign:"top",textAlign:'center'}}>
                    <tr>
                        <td style={{padding:'20px'}}>
                            no manifests available
                        </td>
                    </tr>
                </tbody>
            )}
        </table>
    );
}

export default VehicleManifests;